import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchPublicTenantProfile, fetchRegistrationProfile, fetchPublicProfileBySlug } from "../api/tenantProfileApi";
import PublicCustomerRegistration from "../components/PublicCustomerRegistration";

const PublicTenantProfile = ({ registration = false }) => {
  const { tenantId, slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadProfile = async () => {
      setLoading(true);
      setProfile(null);
      setError(null);
      try {
        const response = await (slug
          ? registration ? fetchRegistrationProfile(slug) : fetchPublicProfileBySlug(slug)
          : fetchPublicTenantProfile(tenantId));
        if (isMounted) {
          setProfile(response.data || null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message || "Public profile not found or inactive.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (tenantId || slug) {
      loadProfile();
    }
    return () => {
      isMounted = false;
    };
  }, [tenantId, slug, registration]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[#096dea] border-t-transparent"></div>
          <p className="mt-3 text-sm font-medium text-slate-600">Loading public profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center rounded-3xl bg-white p-8 shadow-sm border border-slate-200">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600">
            ⚠️
          </div>
          <h2 className="mt-4 text-lg font-bold text-slate-900">{registration ? "Registration Unavailable" : "Profile Unavailable"}</h2>
          <p className="mt-2 text-sm text-slate-500">{error || "This profile is inactive or does not exist."}</p>
        </div>
      </div>
    );
  }

  const logoSrc =
    profile.logo_data && profile.logo_mime_type
      ? `data:${profile.logo_mime_type};base64,${profile.logo_data}`
      : null;

  return (
    <div className={`min-h-screen bg-slate-50 px-3 sm:px-6 lg:px-8 ${registration ? "py-4" : "py-12"}`}>
      <div className={`mx-auto overflow-hidden border border-slate-200 bg-white shadow-sm ${registration ? "max-w-4xl rounded-lg" : "max-w-2xl rounded-3xl"}`}>
        <div className={`border-b border-slate-100 bg-slate-900/5 text-center ${registration ? "flex flex-col items-center px-4 py-1 sm:px-6" : "p-8"}`}>
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={profile.heading || "Company Logo"}
              className={`${registration ? "h-20 w-60 max-w-full rounded-lg p-2" : "mx-auto h-20 w-20 rounded-2xl p-2"} border border-slate-200 bg-white object-contain shadow-sm`}
            />
          ) : (
            <div className={`${registration ? "h-20 w-20 rounded-lg" : "mx-auto h-20 w-20 rounded-2xl"} flex items-center justify-center border border-slate-200 bg-white text-2xl font-bold text-slate-400 shadow-sm`}>
              {(profile.heading || "Tenant").charAt(0).toUpperCase()}
            </div>
          )}
          <div className={registration ? "sr-only" : undefined}>
          <h1 className={`${registration ? "sr-only" : "mt-4 text-2xl"} font-bold text-slate-950`}>{profile.heading || "Company Profile"}</h1>
          {!registration && <p className="mt-1 text-xs text-slate-500">Official Tenant Public Profile</p>}
          </div>
        </div>

        {!registration && <div className="space-y-6 p-8">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">About</h3>
            <p className="mt-2 text-sm text-slate-700 whitespace-pre-line leading-relaxed">
              {profile.description || "No description provided."}
            </p>
          </div>
          {profile.registration_enabled && profile.slug && <a
            href={`/public/${encodeURIComponent(profile.slug)}/registration`}
            className="inline-flex rounded-lg bg-[#096dea] px-4 py-2 text-sm font-semibold text-white">
            Register a customer
          </a>}
        </div>}
        {registration && profile.registration_enabled && <PublicCustomerRegistration key={profile.bot_id || tenantId} botId={profile.bot_id || tenantId} />}
      </div>
    </div>
  );
};

export default PublicTenantProfile;
